import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { ModalCloseButton } from './InfoModals';
import {
  isScopePathIncluded,
  type GitHubAppConfigResponse,
  type GitHubInstallationSummary,
  type GitHubRepositoryBranchSummary,
  type GitHubRepositoryChoice,
  type GitHubRepositoryDirectorySummary,
  type GitHubRepositorySummary,
  type LoadState,
  type CodebaseSourceMode
} from './workflowShared';

interface CodebaseStepProps {
  headingTitle?: string;
  headingDescription?: string;
  showAdditionalInfo?: boolean;
  additionalInfoButtonLabel?: string;
  additionalInfoTitle?: string;
  additionalInfoContent?: ReactNode;
  additionalInfoButtonPlacement?: 'after-github-action' | 'after-repository-controls';
  githubConnectLabel?: string;
  githubAccessLabel?: string;
  allowManualUpload?: boolean;
  codebaseSourceMode?: CodebaseSourceMode;
  uploadedCodebaseFileName?: string;
  uploadedCodebaseFileSize?: number;
  uploadedCodebaseError?: string;
  githubConfigState: LoadState;
  githubConfigError: string;
  githubConfig: GitHubAppConfigResponse | null;
  githubSessionState: LoadState;
  githubSessionError: string;
  githubSessionId: string;
  githubInstallations: GitHubInstallationSummary[];
  selectedGithubInstallationId: number | null;
  githubRepositoriesState: LoadState;
  githubRepositoriesError: string;
  githubRepositoryChoices: GitHubRepositoryChoice[];
  selectedGithubRepositoryId: number | null;
  githubBranchesState: LoadState;
  githubBranchesError: string;
  githubBranches: GitHubRepositoryBranchSummary[];
  selectedGithubBranch: string;
  githubTreeState: LoadState;
  githubTreeError: string;
  githubTreeDirectories: GitHubRepositoryDirectorySummary[];
  scopePaths: string[];
  nonStandardTokens: 'yes' | 'no' | '';
  trustedRoles: string;
  regularFunctions: string;
  adminRestrictions: string;
  assumptionsFilePath: string;
  externalContractsTrusted: 'yes' | 'no' | '';
  onSelectCodebaseSourceMode?: (mode: CodebaseSourceMode) => void;
  onSelectCodebaseZip?: (file: File | null) => void;
  onConnectGitHub: () => void;
  onInstallGitHubApp: () => void;
  onSelectGithubRepository: (installationId: number, repositoryId: number) => void;
  onSelectGithubBranch: (branch: string) => void;
  onSelectRepositoryRootScope: () => void;
  onToggleScopePath: (scopePath: string) => void;
  onNonStandardTokensChange: (value: 'yes' | 'no') => void;
  onTrustedRolesChange: (value: string) => void;
  onRegularFunctionsChange: (value: string) => void;
  onAdminRestrictionsChange: (value: string) => void;
  onAssumptionsFilePathChange: (value: string) => void;
  onExternalContractsTrustedChange: (value: 'yes' | 'no') => void;
}

interface YesNoFieldProps {
  label: string;
  value: 'yes' | 'no' | '';
  onChange: (value: 'yes' | 'no') => void;
}

function YesNoField({ label, value, onChange }: YesNoFieldProps) {
  const yesActive = value === 'yes';
  const noActive = value === 'no';

  return (
    <label className="field">
      <span>{label}</span>
      <div className="codebase-toggle-row">
        <button
          type="button"
          className={`codebase-toggle-button ${yesActive ? 'active' : ''}`}
          aria-pressed={yesActive}
          onClick={() => onChange('yes')}
        >
          Yes
        </button>
        <button
          type="button"
          className={`codebase-toggle-button ${noActive ? 'active' : ''}`}
          aria-pressed={noActive}
          onClick={() => onChange('no')}
        >
          No
        </button>
      </div>
    </label>
  );
}

function CodebaseGitHubIcon() {
  return (
    <svg className="button-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.58 2 12.24c0 4.52 2.86 8.35 6.84 9.7.5.09.68-.22.68-.49 0-.24-.01-1.04-.01-1.89-2.51.47-3.16-.63-3.36-1.21-.11-.3-.6-1.21-1.03-1.45-.35-.19-.85-.66-.01-.67.79-.01 1.35.74 1.54 1.05.9 1.55 2.34 1.11 2.91.85.09-.67.35-1.11.64-1.37-2.22-.26-4.55-1.14-4.55-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.71 0 0 .84-.28 2.75 1.05A9.28 9.28 0 0 1 12 6.95c.85 0 1.71.12 2.51.34 1.91-1.33 2.75-1.05 2.75-1.05.55 1.41.2 2.45.1 2.71.64.72 1.03 1.63 1.03 2.75 0 3.93-2.34 4.8-4.57 5.06.36.32.68.94.68 1.9 0 1.37-.01 2.47-.01 2.81 0 .27.18.59.69.49A10.12 10.12 0 0 0 22 12.24C22 6.58 17.52 2 12 2Z"
      />
    </svg>
  );
}

function CodebaseUploadIcon() {
  return (
    <svg className="button-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 4v10m0-10 4 4m-4-4-4 4M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

const formatInstallationName = (installation: GitHubInstallationSummary): string => {
  return installation.account.login || `Installation ${installation.id}`;
};

const formatRepositoryLabel = (repository: GitHubRepositorySummary): string => {
  return `${repository.fullName}${repository.private ? ' (private)' : ''}`;
};

const formatUploadSize = (sizeBytes: number | undefined): string => {
  if (!sizeBytes || sizeBytes <= 0) {
    return '';
  }

  if (sizeBytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(sizeBytes / 1024)).toLocaleString()} KB`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getScopeParentPath = (scopePath: string): string | null => {
  const parentSeparatorIndex = scopePath.lastIndexOf('/');

  return parentSeparatorIndex === -1 ? null : scopePath.slice(0, parentSeparatorIndex);
};

const getScopeAncestorPaths = (scopePath: string): string[] => {
  const ancestors: string[] = [];
  let parentPath = getScopeParentPath(scopePath);

  while (parentPath) {
    ancestors.unshift(parentPath);
    parentPath = getScopeParentPath(parentPath);
  }

  return ancestors;
};

function ScopeTreeChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`scope-tree-chevron ${expanded ? 'expanded' : ''}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M9 5l7 7-7 7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.2"
      />
    </svg>
  );
}

interface ScopeFolderTreeProps {
  githubTreeState: LoadState;
  githubTreeError: string;
  directories: GitHubRepositoryDirectorySummary[];
  scopePaths: string[];
  onSelectRepositoryRoot: () => void;
  onToggleScopePath: (scopePath: string) => void;
}

function ScopeFolderTree({
  githubTreeState,
  githubTreeError,
  directories,
  scopePaths,
  onSelectRepositoryRoot,
  onToggleScopePath
}: ScopeFolderTreeProps) {
  const [isRootExpanded, setIsRootExpanded] = useState(true);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set());
  const isRootSelected = scopePaths.length === 0;
  const directoryPathSet = useMemo(() => {
    return new Set(directories.map((directory) => directory.path));
  }, [directories]);
  const directoryPathsWithChildren = useMemo(() => {
    const pathsWithChildren = new Set<string>();

    directories.forEach((directory) => {
      const parentPath = getScopeParentPath(directory.path);

      if (parentPath && directoryPathSet.has(parentPath)) {
        pathsWithChildren.add(parentPath);
      }
    });

    return pathsWithChildren;
  }, [directories, directoryPathSet]);
  const visibleDirectories = useMemo(() => {
    if (!isRootExpanded) {
      return [];
    }

    return directories.filter((directory) => {
      return getScopeAncestorPaths(directory.path).every((ancestorPath) => expandedPaths.has(ancestorPath));
    });
  }, [directories, expandedPaths, isRootExpanded]);

  useEffect(() => {
    setIsRootExpanded(true);
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set<string>();

      currentExpandedPaths.forEach((expandedPath) => {
        if (directoryPathSet.has(expandedPath)) {
          nextExpandedPaths.add(expandedPath);
        }
      });

      return nextExpandedPaths;
    });
  }, [directoryPathSet]);

  useEffect(() => {
    if (scopePaths.length === 0) {
      return;
    }

    setIsRootExpanded(true);
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set(currentExpandedPaths);

      scopePaths.forEach((scopePath) => {
        getScopeAncestorPaths(scopePath).forEach((ancestorPath) => {
          if (directoryPathSet.has(ancestorPath)) {
            nextExpandedPaths.add(ancestorPath);
          }
        });
      });

      return nextExpandedPaths;
    });
  }, [scopePaths, directoryPathSet]);

  const toggleRootExpansion = () => {
    setIsRootExpanded((currentlyExpanded) => !currentlyExpanded);
  };

  const toggleDirectoryExpansion = (scopePath: string) => {
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set(currentExpandedPaths);

      if (nextExpandedPaths.has(scopePath)) {
        nextExpandedPaths.delete(scopePath);
      } else {
        nextExpandedPaths.add(scopePath);
      }

      return nextExpandedPaths;
    });
  };

  return (
    <div className="repository-scope-picker">
      <span className="repository-picker-label">Folders in scope</span>
      <div className="scope-tree" role="tree" aria-label="Repository folders">
        <div
          role="treeitem"
          aria-level={1}
          aria-expanded={directories.length > 0 ? isRootExpanded : undefined}
          aria-selected={isRootSelected}
          className={`scope-tree-row root ${isRootSelected ? 'selected' : ''}`}
        >
          <button
            type="button"
            className={`scope-tree-disclosure ${directories.length === 0 ? 'placeholder' : ''}`}
            aria-label={isRootExpanded ? 'Collapse repository root' : 'Expand repository root'}
            onClick={toggleRootExpansion}
            disabled={directories.length === 0}
          >
            <ScopeTreeChevronIcon expanded={isRootExpanded} />
          </button>
          <button
            type="button"
            className="scope-tree-select"
            onClick={onSelectRepositoryRoot}
          >
            <span className={`scope-tree-check ${isRootSelected ? 'checked' : ''}`} aria-hidden="true" />
            <span className="scope-tree-name">Repository root</span>
          </button>
        </div>

        {githubTreeState === 'loading' && (
          <p className="codebase-status scope-tree-status">Loading folders...</p>
        )}
        {githubTreeState === 'error' && (
          <p className="codebase-status error scope-tree-status">{githubTreeError}</p>
        )}
        {githubTreeState === 'ready' && directories.length === 0 && (
          <p className="codebase-status scope-tree-status">No folders found.</p>
        )}
        {githubTreeState === 'ready' && visibleDirectories.map((directory) => {
          const hasChildren = directoryPathsWithChildren.has(directory.path);
          const isExpanded = expandedPaths.has(directory.path);
          const isSelected = isRootSelected || isScopePathIncluded(scopePaths, directory.path);
          const isExactSelection = !isRootSelected && scopePaths.includes(directory.path);

          return (
            <div
              role="treeitem"
              aria-level={directory.depth + 1}
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-selected={isSelected}
              className={`scope-tree-row ${isSelected ? 'selected' : ''} ${isExactSelection ? 'exact' : ''}`}
              key={directory.path}
              style={{ paddingLeft: `${12 + Math.max(0, directory.depth - 1) * 18}px` }}
            >
              <button
                type="button"
                className={`scope-tree-disclosure ${hasChildren ? '' : 'placeholder'}`}
                aria-label={isExpanded ? `Collapse ${directory.name}` : `Expand ${directory.name}`}
                onClick={() => toggleDirectoryExpansion(directory.path)}
                disabled={!hasChildren}
              >
                <ScopeTreeChevronIcon expanded={isExpanded} />
              </button>
              <button
                type="button"
                className="scope-tree-select"
                onClick={() => onToggleScopePath(directory.path)}
              >
                <span className={`scope-tree-check ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
                <span className="scope-tree-name">{directory.name}</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CodebaseStep({
  headingTitle = 'Add your codebase',
  headingDescription = 'Select a GitHub repository.',
  showAdditionalInfo = true,
  additionalInfoButtonLabel = 'Add additional codebase information',
  additionalInfoTitle = 'Contract information',
  additionalInfoContent,
  additionalInfoButtonPlacement = 'after-repository-controls',
  githubConnectLabel = 'Login with GitHub',
  githubAccessLabel = 'Access repo',
  allowManualUpload = false,
  codebaseSourceMode = 'github',
  uploadedCodebaseFileName = '',
  uploadedCodebaseFileSize,
  uploadedCodebaseError = '',
  githubConfigState,
  githubConfigError,
  githubConfig,
  githubSessionState,
  githubSessionError,
  githubSessionId,
  githubInstallations,
  selectedGithubInstallationId,
  githubRepositoriesState,
  githubRepositoriesError,
  githubRepositoryChoices,
  selectedGithubRepositoryId,
  githubBranchesState,
  githubBranchesError,
  githubBranches,
  selectedGithubBranch,
  githubTreeState,
  githubTreeError,
  githubTreeDirectories,
  scopePaths,
  nonStandardTokens,
  trustedRoles,
  regularFunctions,
  adminRestrictions,
  assumptionsFilePath,
  externalContractsTrusted,
  onSelectCodebaseSourceMode,
  onSelectCodebaseZip,
  onConnectGitHub,
  onInstallGitHubApp,
  onSelectGithubRepository,
  onSelectGithubBranch,
  onSelectRepositoryRootScope,
  onToggleScopePath,
  onNonStandardTokensChange,
  onTrustedRolesChange,
  onRegularFunctionsChange,
  onAdminRestrictionsChange,
  onAssumptionsFilePathChange,
  onExternalContractsTrustedChange
}: CodebaseStepProps) {
  const [isContractInfoOpen, setIsContractInfoOpen] = useState(false);
  const activeCodebaseSourceMode = allowManualUpload ? codebaseSourceMode : 'github';
  const isUploadMode = activeCodebaseSourceMode === 'upload';
  const hasGithubSession = Boolean(githubSessionId);
  const selectedGithubInstallation = githubInstallations.find((installation) => {
    return installation.id === selectedGithubInstallationId;
  });
  const hasGitHubInstallUrl = Boolean(githubConfig?.installUrl ?? selectedGithubInstallation?.htmlUrl);
  const githubActionLabel = githubSessionId ? githubAccessLabel : githubConnectLabel;
  const githubAction = githubSessionId ? onInstallGitHubApp : onConnectGitHub;
  const githubActionDisabled = githubSessionId ? !hasGitHubInstallUrl : !githubConfig?.connectUrl;
  const uploadSizeLabel = formatUploadSize(uploadedCodebaseFileSize);
  const closeContractInfo = () => setIsContractInfoOpen(false);
  const selectUploadMode = () => {
    onSelectCodebaseSourceMode?.('upload');
  };
  const selectGitHubMode = () => {
    if (!hasGithubSession) {
      onConnectGitHub();
      return;
    }

    onSelectCodebaseSourceMode?.('github');
  };
  const defaultAdditionalInfoContent = (
    <div className="codebase-questionnaire">
      <YesNoField
        label="Do the contracts integrate with non ERC20 tokens / non standard tokens?"
        value={nonStandardTokens}
        onChange={onNonStandardTokensChange}
      />

      <label className="field">
        <span>Do the contracts have trusted/privileged roles?</span>
        <textarea
          className="codebase-textarea"
          value={trustedRoles}
          placeholder="Describe any trusted or privileged roles..."
          onChange={(event) => onTrustedRolesChange(event.target.value)}
          rows={3}
        />
      </label>

      <label className="field">
        <span>Do the contracts have functions that should be executed regularly?</span>
        <textarea
          className="codebase-textarea"
          value={regularFunctions}
          placeholder="Describe any functions that need regular execution..."
          onChange={(event) => onRegularFunctionsChange(event.target.value)}
          rows={3}
        />
      </label>

      <label className="field">
        <span>Are there restrictions on values set by admin/owner?</span>
        <textarea
          className="codebase-textarea"
          value={adminRestrictions}
          placeholder="Describe any restrictions on admin/owner values..."
          onChange={(event) => onAdminRestrictionsChange(event.target.value)}
          rows={3}
        />
      </label>

      <label className="field">
        <span>Repository assumptions file path</span>
        <input
          type="text"
          value={assumptionsFilePath}
          placeholder="docs/assumptions.md"
          onChange={(event) => onAssumptionsFilePathChange(event.target.value)}
        />
      </label>

      <YesNoField
        label="Are external integrated contracts trusted?"
        value={externalContractsTrusted}
        onChange={onExternalContractsTrustedChange}
      />
    </div>
  );
  const additionalInfoButton = showAdditionalInfo ? (
    <button
      type="button"
      className="secondary-button codebase-additional-button"
      onClick={() => setIsContractInfoOpen(true)}
    >
      {additionalInfoButtonLabel}
    </button>
  ) : null;

  return (
    <>
      <div className="step-heading">
        <h1>{headingTitle}</h1>
        <p>{headingDescription}</p>
      </div>

      <div className="codebase-area">
        {allowManualUpload && (
          <div className="codebase-source-actions" aria-label="Codebase source">
            <button
              type="button"
              className={`codebase-source-action ${activeCodebaseSourceMode === 'upload' ? 'active' : ''}`}
              aria-pressed={activeCodebaseSourceMode === 'upload'}
              onClick={selectUploadMode}
            >
              <CodebaseUploadIcon />
              <span>Codebase upload</span>
            </button>
            <button
              type="button"
              className={`codebase-source-action ${activeCodebaseSourceMode === 'github' ? 'active' : ''}`}
              aria-pressed={activeCodebaseSourceMode === 'github'}
              onClick={selectGitHubMode}
              disabled={!hasGithubSession && !githubConfig?.connectUrl}
            >
              <CodebaseGitHubIcon />
              <span>{hasGithubSession ? 'GitHub repo' : 'GitHub login'}</span>
            </button>
          </div>
        )}

        {isUploadMode ? (
          <div className="github-access manual-codebase-upload">
            {!allowManualUpload && (
              <>
                {!githubSessionId ? (
                  <button
                    type="button"
                    className="primary-button github-access-button"
                    onClick={onConnectGitHub}
                    disabled={!githubConfig?.connectUrl}
                  >
                    <CodebaseGitHubIcon />
                    <span>{githubConnectLabel}</span>
                  </button>
                ) : githubSessionState === 'ready' ? (
                  <p className="codebase-status">GitHub login connected.</p>
                ) : null}

                {githubConfigState === 'loading' && (
                  <p className="codebase-status">Loading GitHub App configuration...</p>
                )}
                {githubConfigState === 'error' && (
                  <p className="codebase-status error">{githubConfigError}</p>
                )}
                {githubConfigState === 'ready' && githubConfig && !githubConfig.connectUrl && (
                  <p className="codebase-status error">
                    GitHub login is not configured.
                  </p>
                )}
                {githubSessionError && (
                  <p className="codebase-status error">{githubSessionError}</p>
                )}
                {githubSessionState === 'loading' && (
                  <p className="codebase-status">Loading GitHub login...</p>
                )}
              </>
            )}

            <label className="field codebase-upload-field">
              <span>Codebase zip</span>
              <input
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                onChange={(event) => onSelectCodebaseZip?.(event.target.files?.[0] ?? null)}
              />
            </label>

            {uploadedCodebaseFileName && (
              <p className="codebase-status">
                Selected {uploadedCodebaseFileName}{uploadSizeLabel ? ` (${uploadSizeLabel})` : ''}
              </p>
            )}
            {uploadedCodebaseError && (
              <p className="codebase-status error">{uploadedCodebaseError}</p>
            )}

            {additionalInfoButtonPlacement === 'after-repository-controls' && additionalInfoButton}
          </div>
        ) : (
          <div className="github-access">
          {(!allowManualUpload || hasGithubSession) ? (
            <button
              type="button"
              className="primary-button github-access-button"
              onClick={githubAction}
              disabled={githubActionDisabled}
            >
              <CodebaseGitHubIcon />
              <span>{githubActionLabel}</span>
            </button>
          ) : (
            <p className="codebase-status">Use GitHub login to connect a repository.</p>
          )}

          {additionalInfoButtonPlacement === 'after-github-action' && additionalInfoButton}

          {githubConfigState === 'loading' && (
            <p className="codebase-status">Loading GitHub App configuration...</p>
          )}
          {githubConfigState === 'error' && (
            <p className="codebase-status error">{githubConfigError}</p>
          )}
          {githubConfigState === 'ready' && githubConfig && !githubConfig.configured && (
            <p className="codebase-status error">
              Missing GitHub App config: {(githubConfig.missing ?? []).join(', ')}
            </p>
          )}
          {githubSessionError && (
            <p className="codebase-status error">{githubSessionError}</p>
          )}
          {githubSessionState === 'loading' && (
            <p className="codebase-status">Loading GitHub installations...</p>
          )}

          {githubSessionId && githubSessionState === 'ready' && githubInstallations.length === 0 && (
            <p className="codebase-status">No GitHub App installations found.</p>
          )}

          {githubSessionId && githubSessionState === 'ready' && githubInstallations.length > 0 && (
            <div className="repository-picker">
              <span className="repository-picker-label">Repository</span>
              {githubRepositoriesState === 'loading' ? (
                <p className="codebase-status">Loading repositories...</p>
              ) : githubRepositoriesState === 'ready' ? (
                <div className="repository-choice-list" role="listbox" aria-label="GitHub repositories">
                  {githubRepositoryChoices.map(({ installation, repository }) => {
                    const isSelected = installation.id === selectedGithubInstallationId &&
                      repository.id === selectedGithubRepositoryId;
                    const repositoryLabel = `${formatInstallationName(installation)} - ${formatRepositoryLabel(repository)}`;

                    return (
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`repository-choice ${isSelected ? 'selected' : ''}`}
                        key={`${installation.id}-${repository.id}`}
                        onClick={() => onSelectGithubRepository(installation.id, repository.id)}
                      >
                        <span>{repositoryLabel}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          )}

          {githubRepositoriesState === 'error' && (
            <p className="codebase-status error">{githubRepositoriesError}</p>
          )}
          {githubInstallations.length > 0 && githubRepositoriesState === 'ready' && githubRepositoryChoices.length === 0 && (
            <p className="codebase-status">No repositories are available for these installations.</p>
          )}

          {selectedGithubRepositoryId && githubRepositoriesState === 'ready' && (
            <div className="repository-scope-controls">
              <label className="field">
                <span>Branch</span>
                <select
                  value={selectedGithubBranch}
                  onChange={(event) => onSelectGithubBranch(event.target.value)}
                  disabled={githubBranchesState !== 'ready' || githubBranches.length === 0}
                >
                  {githubBranches.length === 0 ? (
                    <option value="">No branches loaded</option>
                  ) : githubBranches.map((branch) => (
                    <option key={branch.name} value={branch.name}>
                      {branch.default ? `${branch.name} (default)` : branch.name}
                    </option>
                  ))}
                </select>
              </label>

              {githubBranchesState === 'loading' && (
                <p className="codebase-status">Loading branches...</p>
              )}
              {githubBranchesState === 'error' && (
                <p className="codebase-status error">{githubBranchesError}</p>
              )}

              <ScopeFolderTree
                githubTreeState={githubTreeState}
                githubTreeError={githubTreeError}
                directories={githubTreeDirectories}
                scopePaths={scopePaths}
                onSelectRepositoryRoot={onSelectRepositoryRootScope}
                onToggleScopePath={onToggleScopePath}
              />
            </div>
          )}

          {additionalInfoButtonPlacement === 'after-repository-controls' && additionalInfoButton}
          </div>
        )}
      </div>

      {showAdditionalInfo && isContractInfoOpen && (
        <div className="modal-overlay" onClick={closeContractInfo}>
          <div
            className="modal-content codebase-info-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="codebase-info-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="codebase-info-title">{additionalInfoTitle}</h2>
              <ModalCloseButton onClose={closeContractInfo} />
            </div>

            <div className="modal-body">
              {additionalInfoContent ?? defaultAdditionalInfoContent}
            </div>

            <div className="modal-footer">
              <button type="button" className="primary-button" onClick={closeContractInfo}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CodebaseStep;
